import { useEffect, useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Lock } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const card = {
  hidden: { opacity: 0, y: 32 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

const inputCls =
  "w-full border border-white/15 bg-[#0A0A0A] px-4 py-3.5 text-sm text-white placeholder:text-zinc-600 outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#E60000] focus:shadow-[0_0_0_3px_rgba(230,0,0,0.15)]";

export const Login = () => {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/account", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      navigate("/account", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center bg-[#050505] px-5 py-16 text-white">
      <div className="noise-overlay" aria-hidden="true" />
      <Link
        to="/"
        data-testid="auth-back-to-site-link"
        className="absolute left-5 top-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-zinc-400 transition-colors duration-300 hover:text-[#E60000] md:left-10"
      >
        <ArrowLeft size={15} />
        Back to site
      </Link>

      <motion.div
        variants={card}
        initial="hidden"
        animate="show"
        className="w-full max-w-md border border-white/10 bg-white/[0.03] p-8 md:p-10"
      >
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>

        <div className="mb-8 grid grid-cols-2 gap-px border border-white/10 bg-white/10">
          {["login", "register"].map((m) => (
            <button
              key={m}
              onClick={() => {
                setMode(m);
                setError("");
              }}
              data-testid={`auth-tab-${m}`}
              className={`py-3 font-display text-xs font-semibold uppercase tracking-[0.25em] transition-[background-color,color] duration-300 ${
                mode === m ? "bg-[#E60000] text-white" : "bg-[#0A0A0A] text-zinc-400 hover:text-white"
              }`}
            >
              {m === "login" ? "Log in" : "Register"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid={`auth-form-${mode}`}>
          {mode === "register" && (
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              required
              data-testid="auth-name-input"
              className={inputCls}
            />
          )}
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
            required
            data-testid="auth-email-input"
            className={inputCls}
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={mode === "register" ? "Password (min 8 characters)" : "Password"}
            required
            minLength={8}
            data-testid="auth-password-input"
            className={inputCls}
          />

          {error && (
            <div
              role="alert"
              data-testid="auth-error-alert"
              className="border border-[#E60000]/50 bg-[#E60000]/10 px-4 py-3 text-sm text-[#FF1A1A]"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid={`auth-${mode}-submit-button`}
            className="flex w-full items-center justify-center gap-2 bg-[#E60000] py-4 font-display text-sm font-semibold uppercase tracking-[0.25em] text-white transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-[#FF1A1A] hover:shadow-[0_12px_40px_rgba(230,0,0,0.35)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Lock size={15} />
            {busy ? "One moment…" : mode === "login" ? "Log in" : "Create account"}
          </button>
        </form>

        <p className="mt-8 text-center text-xs leading-relaxed text-zinc-500">
          Owner & team access for MARR Mobile Detailing.
          <br />
          Customers can book directly on the site — no account needed.
        </p>
      </motion.div>
    </div>
  );
};

export default Login;
